import { Track } from 'vue-media-annotator';
import { computed } from '@vue/composition-api';

import { Annotation, ReadonlyDataset, RectBounds } from '@/types';

// eslint-disable-next-line import/no-cycle
import store from '@/store';
import {
  trackMap,
  sortedTracks,
  selectTrack,
  addTrack,
  insertTrack,
  removeTrack,
  selectedTrackId,
} from './state';

export const coordinatesEqual = (arr1: RectBounds | undefined, arr2: RectBounds | undefined) => (
  arr1 !== undefined
  && arr2 !== undefined
  && arr1.length === arr2.length
  && arr1.every((val, i) => arr2[i] === val)
);

export function annotationEqualsFeature(annotation: Annotation, track: Track, frame: number, ignoreClass = false) {
  // Need to round coordinates to match the rounded values in featureBounds

  const [feature] = track.getFeature(frame);
  if (feature === null) { return false; }

  const annotationBounds = annotation.coordinates.map((coord) => Math.round(coord)) as RectBounds;
  const classMatch = ignoreClass || (!!track.confidencePairs.length && track.confidencePairs[0][0] === annotation.class);
  return classMatch && coordinatesEqual(feature.bounds, annotationBounds);
}

export function annotationBelongsToTrack(frame: number | null, annotation: Annotation, track: Track, ignoreClass = false) {
  if (frame === null) { return false; }

  return annotationEqualsFeature(annotation, track, frame, ignoreClass);
}

/**
 * Check if any other tracks have a feature on this frame with the same class,
 * and only 1 feature (the one on this frame).
 *
 * If so, instead of deleting this track's feature, transfer ownership of the other
 * track's single feature to this track, and delete the other track.
 * This ensures as few tracks as possible.
 */
export function deleteTrackFeature(track: Track, frame: number) {
  let containingTrack = track;
  if (track.featureIndex.length > 1) {
    const possibleTracks = sortedTracks.value.filter(
      (otherTrack) => (
        track.trackId !== otherTrack.trackId
        && otherTrack.getFeature(frame)[0]
        && otherTrack.featureIndex.length === 1
        && track.getType()?.[0] === otherTrack.getType()?.[0]
      ),
    );

    if (possibleTracks.length) {
      // Set containingTrack to the first possible track
      [containingTrack] = possibleTracks;

      // Set the other track's feature on this one
      // `!` used because we know from above that the feature isn't null
      track.setFeature(containingTrack.getFeature(frame)[0]!);
    }
  }

  // Deselect track before removing the feature/track
  if (selectedTrackId.value === containingTrack.trackId) {
    selectTrack(null);
  }

  // Check if this is last track feature
  if (containingTrack.featureIndex.length === 1) {
    // Remove track if last feature
    removeTrack(containingTrack.trackId);
  } else {
    // Just delete feature
    containingTrack.deleteFeature(frame);
  }
}

/**
 * Transfer ownership of the specified track's current frame feature to another track.
 * If no other track is able to take ownership, a new track is created.
 * @param trackId The trackId of the track that's transferring feature ownership
 * @returns The trackId of the track which now owns the feature
 */
export function transferTrackFeatureOwnership(trackId: number, trackType: string): number {
  const { currentFrameIndex } = store.state.dataset;

  const currentTrack = trackMap.get(trackId);
  if (!currentTrack || currentFrameIndex === null) {
    throw new Error('Must have a valid track and frame to operate on!');
  }

  // Ensure current track type and feature are defined
  const currentTrackFeature = currentTrack?.getFeature(currentFrameIndex)[0];
  if (!currentTrackFeature) {
    throw new Error('Must have a valid track feature to operate on!');
  }

  // If just added, make sure it's not a duplicate
  const possibleTracks = sortedTracks.value.filter(
    (track) => (
      track.trackId !== currentTrack.trackId
      && trackType === track.getType()?.[0]
      && track.getFeature(currentFrameIndex)[0] === null
    ),
  );

  // If no possible track candidates, create a new track
  const existingTrack = possibleTracks.length
    ? possibleTracks[0]
    : addTrack(currentFrameIndex, trackType);

  // Assign the feature to the existing track
  existingTrack.setFeature(currentTrackFeature);
  selectTrack(existingTrack.trackId);

  // Delete the feature from the current track
  deleteTrackFeature(currentTrack, currentFrameIndex);

  return existingTrack.trackId;
}

/**
 * With this data, tracks correspond to instances of an annotation class.
 * For example, each ear would have its own track, body would have its own track, etc.
 * @param dataset - The dataset to convert to a list of tracks
 */
export function datasetToTracks(dataset: ReadonlyDataset): Track[] {
  // Maps each annotation class to a list of track ids that have been used to represent this
  const classToTrackIds: {[cls: string]: number[]} = {};
  const tracks: Track[] = [];

  dataset.forEach((frame, index) => {
    // A mapping of the current number of occurences of each class in this frame
    const frameClassOccurences: {[cls: string]: number} = {};
    frame.annotations.regions.forEach((annotation) => {
      // Assume type is 'box'
      const { coordinates, class: annotationClass } = annotation;

      if (classToTrackIds[annotationClass] === undefined) {
        classToTrackIds[annotationClass] = [];
      }

      const trackIds = classToTrackIds[annotationClass];
      const feature = {
        frame: index,
        interpolate: false,
        keyframe: true,
        bounds: coordinates,
        attributes: { confidence: annotation.model_score },
      };

      // Occurences is the number of occurences seen so far, including this one
      const occurences = frameClassOccurences[annotationClass]
        ? frameClassOccurences[annotationClass] + 1
        : 1;
      frameClassOccurences[annotationClass] = occurences;

      // If the number of tracks used so far is less than the amount of occurences
      // of the class so far in this frame, we need to add another track
      if (occurences > trackIds.length) {
        const track = new Track(tracks.length, { begin: index, end: index, confidencePairs: [[annotationClass, 1.0]] });
        track.setFeature(feature);

        tracks.push(track);
        classToTrackIds[annotationClass].push(track.trackId);
      } else {
        // Otherwise, just update an existing track
        // Use the number of frame occurances to index the trackIds
        const trackId = trackIds[occurences - 1];
        tracks[trackId].setFeature(feature);
      }
    });
  });

  return tracks;
}

/**
 * Initialize tracks from the dataset
 */
export function initializeTracksFromDataset() {
  const { dataset } = store.state.dataset;
  const tracks = dataset ? datasetToTracks(dataset) : [];
  selectTrack(null, false);

  // Remove old tracks
  sortedTracks.value.forEach((track) => {
    removeTrack(track.trackId);
  });

  // Add tracks to track map
  tracks.forEach((track) => {
    insertTrack(track);
  });
}

/**
 * COMPUTED VARS
 */

/**
 * Map track IDs to annotation indices
 */
export const trackIdToAnnotationIndex = computed((): { [key: number]: number } | null => {
  const { currentFrameIndex } = store.state.dataset;
  const { currentFrameAnnotations } = store.getters.dataset;
  if (currentFrameIndex === null || currentFrameAnnotations === null) {
    return null;
  }

  const tracksToAnnotations = sortedTracks.value.reduce((obj, track) => {
    const annotationIndex = currentFrameAnnotations.findIndex(
      (annotation) => annotationBelongsToTrack(currentFrameIndex, annotation, track, true),
    );

    const value = annotationIndex !== -1 ? annotationIndex : null;
    return { ...obj, [track.trackId]: value };
  }, {});

  return tracksToAnnotations;
});

/**
 * Map annotation indices to track IDs
 */
export const annotationIndexToTrackId = computed((): {[key: number]: number} | null => {
  const { currentFrameIndex } = store.state.dataset;
  const { currentFrameAnnotations } = store.getters.dataset;
  if (currentFrameIndex === null || currentFrameAnnotations === null) {
    return null;
  }

  const annotationsToTracks = currentFrameAnnotations.reduce((obj, annotation, index) => {
    const matchingTrack = sortedTracks.value.find(
      (track) => annotationBelongsToTrack(currentFrameIndex, annotation, track, true),
    );

    return { ...obj, [index]: matchingTrack?.trackId ?? null };
  }, {});

  return annotationsToTracks;
});
